import { FlexColumn } from '@fox/layout/src/flex';
import { FormLabel } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { roundNumber } from '../../utils/number-functions';
import { ExtendedTextFieldProps, TextField } from '../text-field/index';

const NumberInputLabel = styled(FormLabel)`
	margin-bottom: 0.25rem;
`;

export type NumberUnitLabel = 'px' | '%' | (string & {});

type NumberInputSettings = {
	/**
	 * If true, disable clearing values. If attempted to clear, sets value to 0 if zeroDefault is true, otherwise to min or 0.
	 */
	disableClearable?: boolean;

	/**
	 * Minimum value. This is an html attribute for the input element.
	 */
	min?: number;

	/**
	 * Maximum value. This is an html attribute for the input element.
	 */
	max?: number;

	/**
	 * The increment that the arrow buttons increase and decrease the value by. Also applies to a focused input
	 * when using arrow keys or scroll wheel. Use 'any' to allow any decimal value without step validation.
	 */
	step?: number | 'any';

	/**
	 * A callback to be fired on on blur of component. This is when we do any number constraining and transformation.
	 */
	onChange?: (val: number | undefined) => void;

	/**
	 * The value of the input.
	 */
	value?: number;

	/**
	 * If true, the input will default to 0 zero when no value is entered
	 * (by default, this controlled component defaults to empty string).
	 */
	zeroDefault?: boolean;

	/**
	 * An optional function to be fired during the onBlur event after internal handleBlur processing.
	 */
	onBlur?: (val: number | undefined) => void;

	/**
	 * Rounds input to specified decimal places
	 */
	fixedDecimals?: number;
};

export type NumberInputProps = NumberInputSettings &
	Omit<ExtendedTextFieldProps, 'value' | 'onChange'> & {
		/**
		 * The unit label appended at the end of the input. While any string will be accepted, for consistency's sake,
		 * an existing label from the `NumberUnitLabel` type should be used. If a suitable label isn't available, it should
		 * be added to the type.
		 */
		unitLabel?: NumberUnitLabel;
	};

function useNumberInput({ disableClearable, min, max, step, value, zeroDefault, onChange, onBlur, fixedDecimals }: NumberInputSettings) {
	const defaultValue = zeroDefault ? 0 : '';
	const [fieldValue, setFieldValue] = React.useState<string | number | undefined>(value ?? defaultValue);
	const [rangeError, setRangeError] = React.useState<string | null>(null);
	const hasMin = typeof min === 'number';
	const hasMax = typeof max === 'number';

	useEffect(() => {
		setFieldValue(value);
	}, [value]);

	const getRangeErrorMessage = (min, max) => {
		if (!hasMin) return `Range: < ${max}`;
		if (!hasMax) return `Range: > ${min}`;
		return `Range: ${min} - ${max}`;
	};

	// Tracks whether the user has actually edited the input, and whether a blur has already
	// committed that edit. These let the unmount flush only fire for genuinely pending edits
	// and avoid double-firing when a blur and unmount happen in the same tick.
	const userEditedRef = useRef(false);
	const didBlurRef = useRef(false);

	const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
		didBlurRef.current = true;
		commitRawValue(event.target.value);
	};

	const commitRawValue = (rawValue: string, skipLocalState = false) => {
		// During an unmount flush we must not call setState (it is a no-op that React warns about),
		// so only update local field/range state when committing from a live interaction.
		const commitFieldValue = (next: string | number | undefined) => {
			if (!skipLocalState) setFieldValue(next);
		};
		const commitRangeError = (next: string | null) => {
			if (!skipLocalState) setRangeError(next);
		};

		const value = rawValue !== '' ? Number(rawValue) : undefined;
		const isNumber = typeof value === 'number';
		const fixedValue = fixedDecimals !== undefined && isNumber ? roundNumber(value, fixedDecimals) : value;

		if (!hasMin && !hasMax) {
			onChange?.(fixedValue);
			onBlur?.(fixedValue);
			commitFieldValue(fixedValue);
			return;
		}

		if (!disableClearable && rawValue === '') {
			commitFieldValue('');
			onChange?.(undefined);
			onBlur?.(undefined);
			commitRangeError(null);
			return;
		}

		if (disableClearable && rawValue === '') {
			const defaultVal = zeroDefault ? 0 : (min ?? 0);
			commitFieldValue(defaultVal);
			onChange?.(defaultVal);
			onBlur?.(defaultVal);
			commitRangeError(null);
			return;
		}

		if ((hasMin && fixedValue < min) || (hasMax && fixedValue > max)) {
			// Clamp only to the bounds that exist
			let clampedValue = fixedValue;
			if (hasMax && typeof clampedValue === 'number' && clampedValue > max) {
				clampedValue = max;
			}
			if (hasMin && typeof clampedValue === 'number' && clampedValue < min) {
				clampedValue = min;
			}
			commitFieldValue(clampedValue);
			onChange?.(clampedValue);
			onBlur?.(clampedValue);
			commitRangeError(null);
			return;
		}
		onBlur?.(fixedValue);
		onChange?.(fixedValue);
		commitFieldValue(fixedValue);
	};

	// Flush any pending value when the input unmounts before a blur could fire (e.g. the user
	// types a value then immediately navigates away / deselects the block, which unmounts the
	// settings panel). Without this the typed value is lost and never reaches the parent.
	// Example scenario: when you set a number value in form designer and change to publish tab without ever blurring.
	const flushRef = React.useRef<() => void>();
	flushRef.current = () => {
		// Only flush genuinely pending edits: skip if the user never typed, or if a blur already
		// committed the value (a blur+unmount in the same tick would otherwise double-fire onChange/onBlur
		// because the parent `value` prop has not yet updated to match).
		if (!userEditedRef.current || didBlurRef.current) return;
		const raw = fieldValue === undefined || fieldValue === null ? '' : `${fieldValue}`;
		const committed = value === undefined || value === null ? '' : `${value}`;
		if (raw === committed) return;
		// Skip local setState since the component is unmounting.
		commitRawValue(raw, true);
	};
	useEffect(() => () => flushRef.current?.(), []);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		userEditedRef.current = true;
		didBlurRef.current = false;
		const value = event.target.value === '' ? defaultValue : event.target.value;

		const valueIsEmpty = value === '';

		let localFieldValue: string | number = value;
		let localRangeError: string | null = null;
		if (valueIsEmpty || isNaN(event.target.valueAsNumber)) {
			localFieldValue = defaultValue;
		} else if ((hasMin && Number(value) < min) || (hasMax && Number(value) > max)) {
			localRangeError = getRangeErrorMessage(min, max);
		}

		setFieldValue(localFieldValue);
		setRangeError(localRangeError);
	};
	const currentValue = fieldValue || fieldValue === 0 ? `${fieldValue}` : defaultValue;
	return {
		onChange: handleChange,
		onBlur: handleBlur,
		value: currentValue,
		InputProps: {
			onBlur: handleBlur,
			inputProps: {
				type: 'number',
				step: step,
				min: min,
				max: max
			}
		},
		rangeError
	};
}

/**
 * An extension of the TextField component for numeric values with some common features built in, including clamping of min/max values, unit suffix, and defaulting to 0.
 */
export const ConsistentNumberInput = ({ label, helperText, ...rest }: NumberInputProps) => {
	return (
		<FlexColumn>
			{label ? <NumberInputLabel>{label}</NumberInputLabel> : null}
			<NumberInput
				// Fallback to empty space to retain horizontal alignment when helperText renders beneath
				helperText={helperText ?? ' '}
				{...rest}
			/>
		</FlexColumn>
	);
};

export const NumberInput = ({
	disableClearable,
	min,
	max,
	step,
	unitLabel,
	value,
	zeroDefault,
	helperText,
	onChange,
	onBlur,
	fixedDecimals,
	variant,
	...rest
}: NumberInputProps) => {
	const { rangeError, ...h } = useNumberInput({
		disableClearable,
		min,
		max,
		step,
		value,
		zeroDefault,
		onChange,
		onBlur,
		fixedDecimals
	});
	return (
		<TextField
			size="small"
			error={!!rangeError || rest.error}
			helperText={rangeError ?? helperText}
			endAdornment={unitLabel}
			variant={variant}
			{...rest}
			{...h}
		/>
	);
};
